import type { PropsOf } from '@emotion/react';
import type {
  AudienceDefinitionGenderEnum,
  AudienceDefinitionRankColumnEnum,
  CreateSeedAudienceRequest,
  CreateSeedAudienceRequestIntegrationsInnerIntegrationTypeEnum as IntegrationTypeSDK,
  LookalikeAudienceSizesInner,
  LookalikeRatio,
  MetaDestinationMetadata,
  AdPlatform as SdkAdPlatform,
  AudienceDefinition as SdkAudienceDefinition,
  AudienceDefinitionPreviewResponse as SdkAudienceDefinitionPreviewResponse,
  AudienceDeleteResult as SdkAudienceDeleteResult,
  AudienceDestination as SdkAudienceDestination,
  AudienceTarget as SdkAudienceTarget,
  CreateAudienceRequest as SdkCreateAudienceRequest,
  PreviewCountRequest as SdkPreviewCountRequest,
  AdAccount1 as SdkProximaAdAccount,
  UpdateAudienceRequest as SdkUpdateAudienceRequest,
  SeedAudienceType as SeedAudienceTypeSDK
} from '@innovationdepartment/proxima-sdk-axios';
import type {
  CellRendererProps,
  SelectProps,
  TableProps,
  Tabs
} from '@innovationdepartment/proxima-ui';
import type { IntegrationType } from 'types/integrations';
import type { Required } from 'utility-types';

export type FlavorCategorySchema = {
  id: string;
  name: string;
};

export type GalaxyFlavorSchema = {
  categoryId: string;
  categoryName: string;
  createdAt: string;
  id: string;
  name: string;
  updatedAt: string;
};

export type GalaxySchema = {
  id: string;
  name: string;
  flavorId: string;
  createdAt: string;
  updatedAt: string;
  flavor: GalaxyFlavorSchema;
  isInternal: boolean;
  allowMultipleAudiences: boolean;
  allowedAdAccountIds: string[];
};

export enum BrandLockAudienceOption {
  KeepAudiences = 'keep_audiences',
  RemoveAudiences = 'remove_audiences'
}

export enum AudienceStatus {
  New = 'new',
  Processing = 'processing',
  Ready = 'ready'
}

export enum LookalikeAudienceStatus {
  New = 'New', // Awaiting seed data generation
  Generating = 'Generating', // Seed data is being generated
  Seeding = 'Seeding', // Seed data is being uploaded to the platform
  Delivered = 'Delivered', // Custom audience has been generated and shared with the customer and is ready to be used
  Error = 'Error', // There was an error at any step in the process
  Active = 'Active', // Custom audience has spend increase since last sync
  Inactive = 'Inactive', // Custom audience has no spend increase since last sync
  Deleted = 'Deleted', // Lookalike audience has been deleted
  DeleteFailed = 'Delete Failed', // Delete has been tried and failed in the event for a lookalike audience
  DeletePending = 'Delete Pending', // Delete has started for a lookalike audience and is pending
  /* frontend custom ones */
  Processing = 'Processing' // Custom audience is being processed
}

export enum SeedAudienceStatus {
  New = 'new', // Awaiting seed data generation
  Generating = 'generating', // Seed data is being generated
  Seeding = 'seeding', // Seed data is being uploaded to the platform
  Delivered = 'delivered', // Custom audience has been generated and shared with the customer and is ready to be used
  Error = 'error', // There was an error at any step in the process
  Active = 'active', // Custom audience has spend increase since last sync
  Inactive = 'inactive', // Custom audience has no spend increase since last sync
  Deleted = 'deleted', // Custom audience has been deleted
  DeleteFailed = 'deleteFailed', // Delete has been tried and failed in the event for a custom audience
  DeletePending = 'deletePending', // Delete has started for a custom audience and is pending
  /* frontend custom ones */
  Processing = 'processing' // Custom audience is being processed
}

export type IntegrationAudience = {
  id: string;
  seedAudienceId: string;
  brandId: string;
  adAccountId: string;
  createdByUserId: string;
  integrationType: IntegrationType;
  externalId: string;
  importedAt: string | null;
  lifetimeSpend: number;
  name: string;
  status: string;
  errorCode: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type LookalikeAudienceSchema = {
  id: string;
  name: string;
  brandId: string;
  createdByUserId: string;
  integrationType: IntegrationType;
  seedAudienceId: string;
  integrationAudienceId: string;
  size: number;
  externalId?: string;
  importedAt?: string;
  displayStatus: LookalikeAudienceStatus;
  status: string; // TODO(Jenky): get these from the backend
  lifetimeSpend: number;
  targetCountry: string;
  createdAt: string;
  updatedAt: string;
  errorCode?: string;
};

type SeedAudienceType = SeedAudienceTypeSDK;

export type SeedAudienceSchema = {
  id: string;
  name: string;
  brandId: string;
  createdByUserId: string;
  type: SeedAudienceType;
  galaxyId: GalaxySchema['id'];
  flavorId: string;
  categoryId: string;
  categoryName: string;
  lifetimeSpend: number;
  updateDayFrequency?: number;
  optedOutOfUpdatesAt?: string;
  status: string;
  integrationAudiences: IntegrationAudience[];
  lookalikeAudiences: LookalikeAudienceSchema[];
  createdAt: string;
  updatedAt: string;
  errorCode?: string;
};

/* lookalike sizes (sources size selection) */
export type LookalikeSizeSchema = LookalikeAudienceSizesInner;

export type FetchSeedAudienceSchema = {
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortDirection: string;
  seedAudiences: SeedAudienceSchema[];
};

export type SpendSummaryDataSchema = {
  lifetimeSpend: number;
  monthlyAverageSpend: number;
};

export type DateRangeSpendDataSchema = {
  totalSpend: number;
  averageDailySpend: number;
};

export type AudienceUpdateSettingsSchema = {
  seedAudiences: {
    [seedAudienceId: string]: boolean;
  };
};

export type AudienceSettingsSchema = {
  seedAudienceId: string;
  optedIntoUpdates: boolean;
  updateDayFrequency: number | string;
};

/* ------ ---------------- ------ */
/* ------ components types ------ */
/* ------ ---------------- ------ */
export type AudiencesProps = {
  seedAudiences: SeedAudienceSchema[];
  onShowRenameSeedAudienceModal: (row: SeedAudienceSchema) => void;
  onShowRenameLookalikeAudienceModal: (row: LookalikeAudienceSchema) => void;
  onShowAudienceSettingsModal: (row: SeedAudienceSchema) => void;
  onShowCreateSeedAudiencesModal?: (open?: boolean) => void;
  onShowDeleteSeedAudienceModal: (row: SeedAudienceSchema) => void;
  onShowCreateLookalikeAudienceModal: (row: SeedAudienceSchema) => void;
  onShowDeleteLookalikeAudienceModal: (row: LookalikeAudienceSchema) => void;
};

export type AudiencesHeaderProps = {
  onShowCreateSeedAudiencesModal: AudiencesProps['onShowCreateSeedAudiencesModal'];
};

/* seed audience table components props */
export type SeedAudiencesTableProps = {
  seedAudiences: AudiencesProps['seedAudiences'];
  onShowRenameSeedAudienceModal: AudiencesProps['onShowRenameSeedAudienceModal'];
  onShowRenameLookalikeAudienceModal: AudiencesProps['onShowRenameLookalikeAudienceModal'];
  onShowAudienceSettingsModal: AudiencesProps['onShowAudienceSettingsModal'];
  onShowDeleteSeedAudienceModal: AudiencesProps['onShowDeleteSeedAudienceModal'];
  onShowDeleteLookalikeAudienceModal: AudiencesProps['onShowDeleteLookalikeAudienceModal'];
  onShowCreateLookalikeAudienceModal: AudiencesProps['onShowCreateLookalikeAudienceModal'];
};

export type SeedAudiencesTableViewProps = {
  seedAudiences: AudiencesProps['seedAudiences'];
  config: TableProps<SeedAudienceSchema>['config'];
};

/* lookalike audience table component props */
export type LookalikeAudiencesTableProps = {
  lookalikeAudiences: LookalikeAudienceSchema[];
  onShowRenameLookalikeAudienceModal: (row: LookalikeAudienceSchema) => void;
  onShowDeleteLookalikeAudienceModal: (row: LookalikeAudienceSchema) => void;
};

export type LookalikeAudiencesTableViewProps = Omit<
  LookalikeAudiencesTableProps,
  'onShowRenameLookalikeAudienceModal' | 'onShowDeleteLookalikeAudienceModal'
> & {
  config: TableProps<LookalikeAudienceSchema>['config'];
};

/* props for renderer function/singular cell components */
export type SeedAudienceTableColumnHeader =
  | 'seedAudience'
  | 'platform'
  | 'displayStatus'
  | 'adAccount'
  | 'dateCreated'
  | 'actions'
  | 'isHovered';
type SeedAudienceTableRowProperties = { [key in SeedAudienceTableColumnHeader]: true };

/* props for expand renderer function/singular cell components */
export type LookalikeAudienceTableColumnHeader =
  | 'lookalikeAudience'
  | 'platform'
  | 'displayStatus'
  | 'dateCreated'
  | 'actions'
  | 'isHovered';
type LookalikeAudienceTableRowProperties = { [key in LookalikeAudienceTableColumnHeader]: true };

export type LookalikeAudienceTableRowExtraFunctions = {
  onShowRenameLookalikeAudienceModal: LookalikeAudiencesTableProps['onShowRenameLookalikeAudienceModal'];
  onShowDeleteLookalikeAudienceModal: LookalikeAudiencesTableProps['onShowDeleteLookalikeAudienceModal'];
};

export type SeedAudienceTableRowExtraFunctions = {
  onShowRenameSeedAudienceModal: SeedAudiencesTableProps['onShowRenameSeedAudienceModal'];
  onShowAudienceSettingsModal: SeedAudiencesTableProps['onShowAudienceSettingsModal'];
  onShowDeleteSeedAudienceModal: SeedAudiencesTableProps['onShowDeleteSeedAudienceModal'];
  onShowCreateLookalikeAudienceModal: SeedAudiencesTableProps['onShowCreateLookalikeAudienceModal'];
};

export type SeedAudienceWithIntegrationTypes = SeedAudienceSchema & {
  integrationTypes: IntegrationType[];
};

export type SeedAudienceTableRowProps = SeedAudienceTableRowProperties &
  SeedAudienceWithIntegrationTypes &
  SeedAudienceTableRowProperties;

export type LookalikeAudienceTableRowProps = LookalikeAudienceSchema &
  Omit<LookalikeAudienceTableRowProperties, 'displayStatus'>;

export type AudienceTableCellRendererProps<T> = CellRendererProps<T>;

export type RenameAudienceModalProps = {
  seedAudience?: SeedAudienceSchema;
  lookalikeAudience?: LookalikeAudienceSchema;
  open: boolean;
  onClose: () => void;
  onRenameAudience: (name: string) => void;
};

export type DeleteAudienceModalProps = {
  seedAudience?: SeedAudienceSchema;
  lookalikeAudience?: LookalikeAudienceSchema;
  open: boolean;
  onClose: () => void;
  onDeleteAudience: () => void;
};

export type AudienceSettingsModalProps = {
  seedAudience?: SeedAudienceSchema;
  open: boolean;
  onClose: () => void;
  onAudienceSettingsUpdate: (audienceSettings: AudienceSettingsSchema) => void;
};

export type AudienceSettingsModalViewProps = {
  onClose: () => void;
  isSubmitting: boolean;
};

/** CreateSeedAudiencesModalView */
export type CreateSeedAudienceSchema = CreateSeedAudienceRequest;

export type CreateSeedAudiencesFormSchema = {
  lookalikeSize: number;
  galaxies: GalaxySchema[];
  integrationType: IntegrationTypeSDK;
  adAccountId: string;
  names: Required<CreateSeedAudienceSchema, 'names'>['names'];
  referenceBrandProportionTarget: Required<
    CreateSeedAudienceSchema,
    'referenceBrandProportionTarget'
  >['referenceBrandProportionTarget'];
};

export type Tab = 'categoryShoppers' | 'colal';

export type CreateSeedAudiencesModalProps = {
  show: boolean;
  galaxies: GalaxySchema[];
  onClose: () => void;
  onSeedAudiencesCreation: () => void;
};

export type CreateSeedAudiencesModalViewProps = CreateSeedAudiencesModalProps & {
  tab: Tab;
  tabs: PropsOf<typeof Tabs>['tabs'];
  onTabChange: (nextTab: Tab) => void;
};

/* Category Shoppers tab */
export type CategoryShoppersProps = CreateSeedAudiencesModalProps & { tab: 'categoryShoppers' };

export type CategoryShoppersViewProps = CategoryShoppersProps & {
  canCreate: boolean;
  loading: boolean;
  submitting: boolean;
  flavorCategory?: FlavorCategorySchema;
};

/* COLAL tab */
export type COLALProps = CreateSeedAudiencesModalProps & { tab: 'colal' };

export type COLALViewProps = COLALProps & {
  canCreate: boolean;
  loading: boolean;
};

export type CreateSeedAudiencesCOLALFormSchema = {
  dataSource: 'original' | 'alternative';
  brandId: string;
  adAccountId: string;
  audienceDefaultName: string;
  lookalikeSize: number;
  galaxies: GalaxySchema[];
  integrationType: IntegrationTypeSDK;
  names: Required<CreateSeedAudienceSchema, 'names'>['names'];
  referenceBrandProportionTarget: Required<
    CreateSeedAudienceSchema,
    'referenceBrandProportionTarget'
  >['referenceBrandProportionTarget'];
};

/* Layout props */
export type CreateSeedAudienceCommonLayoutProps = {
  tab: CreateSeedAudiencesModalViewProps['tab'];
  canCreate: CategoryShoppersViewProps['canCreate'];
  flavorCategory?: FlavorCategorySchema;
  loading: boolean;
};

export type CreateSeedAudiencesDataSourceProps = CreateSeedAudienceCommonLayoutProps;

export type CreateSeedAudiencesNamesProps = CreateSeedAudienceCommonLayoutProps;

export type CreateSeedAudiencesLookalikeSizeProps = {
  disabled?: boolean;
  loading: boolean;
  sizes?: number[];
};

export type CreateSeedAudiencesLookalikeSizeViewProps = Omit<
  CreateSeedAudiencesLookalikeSizeProps,
  'sizes'
> & {
  options: SelectProps['options'];
};

/* CreateLookalikeAudienceModal */
export type CreateLookalikeAudienceSchema = {
  seedAudienceId: string;
  integrationType: IntegrationType;
  size: number;
};

export type CreateLookalikeAudienceModalProps = {
  open: boolean;
  onCreateLookalikeAudience: (data: CreateLookalikeAudienceSchema) => Promise<void>;
  onClose: () => void;
  seedAudience?: SeedAudienceSchema;
};

export type CreateNewLookalikeModalViewProps = Omit<
  CreateLookalikeAudienceModalProps,
  'onCreateLookalikeAudience'
> & {
  isSubmitting: boolean;
  loading: boolean;
  sizes: LookalikeSizeSchema[];
};

export type LookalikeSizePreviewProps = {
  galaxy: GalaxySchema;
  size?: number;
};

export type RetryLookalikeAudienceSchema = {
  lookalikeAudienceId: string;
  seedAudienceId: string;
};

export type CreateSeedAudienceAdAccountSelectProps = {
  galaxies: GalaxySchema[];
};
/* tbd */

/* ------ ------------------------------------ ------ */
/* ------ proxima-audiences (Custom Audiences) ------ */
/* ------ ------------------------------------ ------ */
/* proxima-sdk-axios generates the proxima-audiences wire types from its api.yml.
   The types below are the ones where the service accepts or returns more than api.yml states, or that the SDK lacks.
   Each one says how it differs from the SDK type of the same name. */

/**
 * A platform a Custom Audiences delivery targets.
 *
 * The SDK `AdPlatform` is `meta` only, as the service's enum is.
 * This adds `tiktok` ahead of the service widening its enum, and a live delivery to it fails until then.
 */
export type AdPlatform = SdkAdPlatform | 'tiktok';

/**
 * A Proxima-owned ad account, as the service's `AdAccountResponse` returns it.
 *
 * `platform` is the wider `AdPlatform`, so the Deliver-to form keeps dropping non-Meta Proxima accounts once the service lists them.
 */
export type ProximaAdAccount = Omit<SdkProximaAdAccount, 'platform'> & { platform: AdPlatform };

/**
 * Custom audience slots on a Proxima-owned ad account, as proxima-audiences reads them from Meta.
 *
 * A slot is room for one more seed audience on the account.
 */
export type AdAccountCapacity = NonNullable<ProximaAdAccount['capacity']>;

/** A platform a destination row can hold: any `AdPlatform`, plus `google`, which the service plans next. */
export type DestinationPlatform = AdPlatform | 'google';

/**
 * The filter fields of a count or create request, every field present.
 *
 * Null and `[]` mean "no filter".
 * The SDK `PreviewCountRequest` marks every field optional, and arrays nullable.
 * `purchaseLookbackDays` is any number, since a saved definition can hold a window outside the SDK enum.
 * The service answers such a window with a 400.
 */
export type AudienceFilters = {
  gender: AudienceDefinitionGenderEnum | null;
  ageMin: number | null;
  ageMax: number | null;
  incomeMin: number | null;
  incomeMax: number | null;
  purchaseLookbackDays: number | null;
  commerceAttributes: string[];
  includedBrandIds: string[];
  excludedBrandIds: string[];
  includedStates: string[];
  excludedStates: string[];
  hasDiscountPurchase: boolean | null;
  orderCountMin: number | null;
  orderCountMax: number | null;
  rankColumn: AudienceDefinitionRankColumnEnum | null;
  uniqueIndividualLimit: number | null;
};

/**
 * A saved definition.
 *
 * The SDK types `purchaseLookbackDays` as its enum.
 * The column is a plain integer, so this reads it as any number.
 */
export type AudienceDefinition = Omit<SdkAudienceDefinition, 'purchaseLookbackDays'> & {
  purchaseLookbackDays: number;
};

/** A page of rows, the envelope of the SDK `ListAudiencesResponse` and `ListAudienceSnapshotsResponse`. */
export type Paginated<T> = { data: T[]; pageSize: number; pageNumber: number; total: number };

/** The audience size range Meta last reported for a destination, from the SDK `MetaDestinationMetadata`. */
export type MetaReportedSize = NonNullable<MetaDestinationMetadata['reportedSize']>;

/** A destination row; `platform` is a `DestinationPlatform`, wider than the SDK's `meta` only. */
export type AudienceDestination = Omit<SdkAudienceDestination, 'platform'> & {
  platform: DestinationPlatform;
};

type PreviewResponseBase = Omit<
  SdkAudienceDefinitionPreviewResponse,
  'outcome' | 'name' | 'description'
>;

/** A prompt the filters can express: the filter fields are filled, and a name is generated. */
export type DefinitionPreviewResponse = PreviewResponseBase & {
  outcome: 'definition';
  name: string;
  description: string | null;
};

/** An underspecified prompt: `clarifications` carries the questions, and the filters are empty. */
export type ClarifyPreviewResponse = PreviewResponseBase & {
  outcome: 'clarify';
  name: null;
  description: null;
};

/** Not audience creation, or not expressible: `unmapped` carries the reasons. */
export type DeclinePreviewResponse = PreviewResponseBase & {
  outcome: 'decline';
  name: null;
  description: null;
};

/**
 * A preview, whatever the outcome.
 *
 * The SDK types one flat shape with a nullable `name` on every outcome.
 * The service fills `name` on a `definition` outcome only, so this union narrows on `outcome`.
 * A preview carries no brand fields, since the generator never proposes brand rules.
 */
export type AudienceDefinitionPreviewResponse =
  | DefinitionPreviewResponse
  | ClarifyPreviewResponse
  | DeclinePreviewResponse;

/**
 * One create target.
 *
 * - `platform` is the wider `AdPlatform`.
 * - `lookalikeRatios` is an array. The SDK types it as a `Set`, which axios serializes as `{}`, while the service reads a JSON array.
 */
export type AudienceTarget = Omit<SdkAudienceTarget, 'platform' | 'lookalikeRatios'> & {
  platform: AdPlatform;
  lookalikeRatios?: LookalikeRatio[];
};

/**
 * A count request: `AudienceFilters` plus the brand share.
 *
 * The SDK `PreviewCountRequest` marks every field optional, and this one carries every filter field.
 * An absent or null `brandSharePercent` takes the service's default.
 */
export type PreviewCountRequest = AudienceFilters &
  Pick<SdkPreviewCountRequest, 'brandSharePercent'>;

/**
 * A create request.
 *
 * The SDK marks the filter fields and `nlPrompt` optional and non-nullable.
 * The service's DTO takes null as "not set", so the filters travel verbatim from the edited set.
 */
export type CreateAudienceRequest = AudienceFilters &
  Pick<SdkCreateAudienceRequest, 'name' | 'description' | 'brandSharePercent'> & {
    nlPrompt?: string | null;
    targets: AudienceTarget[];
  };

/**
 * An update request.
 *
 * The SDK types `description` as a string.
 * The service's DTO marks it optional, so null passes validation and clears the column.
 */
export type UpdateAudienceRequest = Omit<SdkUpdateAudienceRequest, 'description'> & {
  description?: string | null;
};

/**
 * The definition after a delete, and what became of each destination that was live when it ran.
 *
 * `definition` is the wider `AudienceDefinition`.
 */
export type AudienceDeleteResult = Omit<SdkAudienceDeleteResult, 'definition'> & {
  definition: AudienceDefinition;
};
